import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { type CriterionValue } from '@spike/automation-model';
import { previewAudienceThunk, AutomationsStatus } from '@spike/automations-action';
import { useApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import Button from 'components/UI/V3/Button/Button';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import styles from './AudiencePreviewModal.module.scss';

const PAGE_SIZE = 30;
const TEMPORAL_EMAIL_DOMAIN = '@temporal.cuddlesapp.com';

const formatEmail = (email?: string) => (email && !email.endsWith(TEMPORAL_EMAIL_DOMAIN) ? email : '—');

interface Props {
    criteria: CriterionValue[];
    onClose: () => void;
}

const AudiencePreviewModal: FunctionComponent<Props> = ({ criteria, onClose }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const [page, setPage] = useState(1);

    const audience = useSelector((state: RootState) => state.automations.audience);
    const pagination = useSelector((state: RootState) => state.automations.audiencePagination);
    const isLoading = useSelector(
        (state: RootState) => state.automations.status === AutomationsStatus.FetchingPreviewAudience
    );

    useEffect(() => {
        (dispatch as any)(previewAudienceThunk(apiClientWrapper, criteria, page, PAGE_SIZE));
    }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <GenericModal
            className={styles.modal}
            onClose={onClose}
            footer={
                <div className={styles.footerContent}>
                    <Button
                        label="Close"
                        variant="outlined"
                        onClick={onClose}
                    />
                </div>
            }
        >
            <div className={styles.body}>
                <h3 className={styles.title}>Clients matching your filters</h3>

                <Table
                    className={styles.table}
                    wrapperClassName={styles.scrollWrapper}
                    fullHeight
                    pagination={{
                        page: pagination?.page ?? page,
                        totalPages: pagination?.totalPages ?? 1,
                        isLoading
                    }}
                    onPageChange={setPage}
                >
                    <TableHeader>
                        <TableHeaderCell className={styles.nameHeaderCell}>Name</TableHeaderCell>
                        <TableHeaderCell className={styles.emailHeaderCell}>Email</TableHeaderCell>
                        <TableHeaderCell className={styles.phoneHeaderCell}>Phone</TableHeaderCell>
                    </TableHeader>

                    <TableBody>
                        {audience.map(recipient => {
                            const fullName = `${recipient.firstName} ${recipient.lastName}`;

                            return (
                                <TableRow key={recipient.id}>
                                    <TableCell
                                        className={styles.nameCell}
                                        title={fullName}
                                    >
                                        {fullName}
                                    </TableCell>
                                    <TableCell
                                        className={styles.emailCell}
                                        title={recipient.email}
                                    >
                                        {formatEmail(recipient.email)}
                                    </TableCell>
                                    <TableCell className={styles.phoneCell}>{recipient.phone || '—'}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </GenericModal>
    );
};

export default AudiencePreviewModal;
